// Header.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import logo from './logo_black.png';
import logoWhite from './Helinox- _White.png';
import useAuth, { setMenuOrderCache } from './useAuth';
import SidebarArrange from './SidebarArrange';
import { applyMenuOrder, mergeMenuOrder } from './menuOrder';
import { useTranslation } from 'react-i18next';
import LanguagePicker from './LanguagePicker';
import { useTheme } from './ThemeContext';
import { useSeason } from './season';
import { XmasClick } from './XmasShower';
import { useLocation, useNavigate } from "react-router-dom";
import { BACKEND_URL } from './backendConfig';
import NotificationBell from './NotificationBell';
import FeedbackButton from './FeedbackButton';
import AskBubble from './AskBubble';
import UserAvatar from './UserAvatar';
import QuoteOfTheDay from './QuoteOfTheDay';
import './QuoteOfTheDay.css';
import AiTag from './AiTag';
import { notifyNew, titleCount } from './gccAlerts';
import { SPL_SCREENS } from './salesPlanningScreens';
import { moduleMenuItem, tabActive } from './moduleTabsConfig';
import { ArrowUpDown, BarChart3, BotMessageSquare, BriefcaseBusiness, CalendarRange, ChevronDown, ClipboardList, FileText, FlaskConical, Factory, Building2, Landmark, Layers, Menu, Moon, Sun, Target, Timer, TreePine } from 'lucide-react';

// Read once: the value is baked in at build time and cannot change while the page runs.
// GTM and Courier have no switch here since 2026-10-21: their entries come from moduleTabsConfig.js, which reads both.
/* Cash Report — staging only until Max says otherwise; the backend half is CASH_MODULE. */
const CASH_ON = process.env.REACT_APP_CASH === '1';
const PARTNER_ON = process.env.REACT_APP_PARTNER === '1';
const DRIVERLOG_ON = process.env.REACT_APP_DRIVERLOG === '1';
const SALESPLAN_ON = process.env.REACT_APP_SALES_PLANNING === '1';
const EXPLORER_ON = process.env.REACT_APP_EXPLORER === '1';
const SALES_CW_ON = process.env.REACT_APP_SALES_CW === '1';
const INVENTORY_ON = process.env.REACT_APP_INVENTORY === '1';
const POLE_ON = process.env.REACT_APP_POLE === '1';
const EUSU_ON = process.env.REACT_APP_EUSU === '1';
const AI_HUB_ON = process.env.REACT_APP_AI_HUB === '1';
const STAFFBUY_ON = process.env.REACT_APP_STAFF_PURCHASE === '1';
const PSR_ON = process.env.REACT_APP_PSR === '1';
const GCC_ON = process.env.REACT_APP_GCC === '1';
// TASK BOARD (Max, 2026-10-30) — staging first; the backend half is TASK_BOARD=1
const TASKS_ON = process.env.REACT_APP_TASKS === '1';
const NOOS_ON = process.env.REACT_APP_NOOS === '1';


// Build-time: the Pricing Approval screen ships to staging only. See scripts/deploy_*.sh.
const PRICING_APPROVAL_ON = process.env.REACT_APP_PRICING_APPROVAL === '1';

/**
 * One collapsible sidebar group.
 *
 * The four groups were four copies of the same markup, and the header button navigated instead of collapsing —
 * the chevron was decoration. This makes it a real disclosure: the header toggles, the chevron turns, and the
 * open/closed state is remembered per group.
 *
 * `defaultOpen` is used the first time a group is seen so that nothing collapses unexpectedly on first visit,
 * and a group is force-opened whenever it contains the page you are on — otherwise navigating into a
 * collapsed group would leave the sidebar showing no trace of where you are.
 */
const SIDEBAR_STATE_KEY = 'portalSidebarGroups';

const readGroupState = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(SIDEBAR_STATE_KEY) || '{}');
        return raw && typeof raw === 'object' ? raw : {};
    } catch (error) {
        return {};                                   // storage disabled or corrupt value
    }
};

function SidebarModule({ id, icon, label, items, isActive, onNavigate, containsActive }) {
    const [open, setOpen] = useState(() => {
        const saved = readGroupState()[id];
        return typeof saved === 'boolean' ? saved : true;
    });

    // Landing inside a collapsed group opens it, so the sidebar always shows where you are.
    useEffect(() => {
        if (containsActive) setOpen(true);
    }, [containsActive]);

    const toggle = () => setOpen((prev) => {
        const next = !prev;
        try {
            localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify({ ...readGroupState(), [id]: next }));
        } catch (error) { /* storage disabled */ }
        return next;
    });

    const navId = `sidebar-nav-${id}`;
    return (
        <div className={`sidebar-module ${open ? 'is-open' : 'is-closed'}`}>
            <button
                type="button"
                className="sidebar-module-header"
                onClick={toggle}
                aria-expanded={open}
                aria-controls={navId}
            >
                {icon}
                <span>{label}</span>
                <ChevronDown size={16} strokeWidth={2} className="sidebar-module-caret" />
            </button>
            {/* Kept in the DOM and hidden with CSS so the open/close can animate and so aria-controls
                always points at something real. */}
            <nav className="sidebar-nav" id={navId} hidden={!open}>
                {items.map((item) => (
                    /* `external` items are not React routes — the user manual is a static HTML file
                       the backend serves, so it opens in its own tab instead of going through the
                       router. Everything else behaves exactly as before. */
                    item.external ? (
                        <a
                            key={item.external}
                            className="sidebar-nav-item"
                            href={item.external}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {item.label}
                        </a>
                    ) : (
                        <button
                            key={item.path}
                            type="button"
                            className={`sidebar-nav-item ${(item.active ?? isActive(item.path)) ? 'active' : ''}`}
                            onClick={() => onNavigate(item.path)}
                        >
                            {item.label}
                            {item.count > 0 && <span className="sidebar-count" data-sidebar-count={item.path}>{item.count > 99 ? '99+' : item.count}</span>}
                        </button>
                    )
                ))}
            </nav>
        </div>
    );
}

/* EVERY SCREEN DRAWS ITS OWN <Header/>, so the header is thrown away and built again on each navigation.
   What a person set on it has to outlive that, or it is undone by the click it was set for (Max, 2026-10-21,
   on a recording: the sidebar jumped back to the top every time an entry low in it was pressed). Kept for the
   life of the page, not stored: a reload starts from the defaults, as it always has. */
const headerMemo = { sidebarOpen: null, sidebarScroll: 0, accessPending: null, accessAt: 0, gccUnread: 0, aiHubManage: null };
// A decided access request changes the badge, so the next header asks again instead of waiting out the minute.
export const forgetAccessBadge = () => { headerMemo.accessAt = 0; };

const Header = () => {
    const { userId, name, roles, roleNames = {}, functions, activeRole, setActiveRole, logout, external, driverOnly, menuOrder, avatarUrl } = useAuth();
    /* A partner's account (DAC VN, Jay 2026-10-17) sees its own track and nothing of HEVN's: no quote, no
       bell, no assistant, no manuals, no profile page — each of those reads an HEVN route the server
       refuses it. */
    const internal = !external;
    const { t } = useTranslation();
    const { resolved: theme, toggleTheme } = useTheme();
    // the Christmas season (season.js): the switch shows only while it is in season (or previewed)
    const season = useSeason();
    const [xmasOpen, setXmasOpen] = useState(false);
    /* the first time somebody meets the season, a note under the tree says where to switch it off (Max 31/10: "user ko muốn thì
       biết tắt liền ở chỗ nào") — once per season per browser */
    const xmasSeasonKey = String(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());
    const [xmasTip, setXmasTip] = useState(() => { try { return localStorage.getItem('portalXmasTip') !== xmasSeasonKey; } catch (e) { return false; } });
    const closeXmasTip = () => { setXmasTip(false); try { localStorage.setItem('portalXmasTip', xmasSeasonKey); } catch (e) { /* private window */ } };
    const xmasRef = useRef(null);
    useEffect(() => {
        if (!xmasOpen) return undefined;
        const away = (e) => { if (xmasRef.current && !xmasRef.current.contains(e.target)) setXmasOpen(false); };
        const esc = (e) => { if (e.key === 'Escape') { setXmasOpen(false); xmasRef.current?.querySelector('button')?.focus(); } };
        document.addEventListener('mousedown', away); document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
    }, [xmasOpen]);
    const can = (key) => Array.isArray(functions) && functions.includes(key);
    const navigate = useNavigate();
    const location = useLocation();
    /* Asked for once, and ONLY by somebody who may act on it: a request per page load for every
       user, to show a badge nobody else can see, is a query the portal does not need. A failure
       leaves it at zero — the menu item is still there and still opens the screen. */
    const [accessPending, setAccessPending] = useState(headerMemo.accessPending || 0);
    /* GCC's unread messages on its menu row, not in the bell (Jay, 29/10: "the alarm is so messy. Can we move the number
       icon for unread # to here?"). Asked on every screen change and each minute, passively — the poll must not keep a
       session alive (see NotificationBell). */
    const [gccUnread, setGccUnread] = useState(headerMemo.gccUnread || 0);
    const gccOn = GCC_ON && Array.isArray(functions) && functions.includes('ai_hub') && !external;
    useEffect(() => {
        if (!gccOn || !userId) return undefined;
        let stop = false;
        const ask = () => fetch(`${BACKEND_URL}/api/gcc/unread`, { credentials: 'include', headers: { 'X-Portal-Passive': '1' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (stop || !d) return;
                headerMemo.gccUnread = Number(d.unread) || 0; setGccUnread(headerMemo.gccUnread);
                // the count on the browser tab, and the desktop alert for whoever switched it on (gccAlerts.js; Max 02/11, #1166)
                titleCount(headerMemo.gccUnread);
                notifyNew(d, { onOpen: (pk) => navigate(`/gcc?room=${pk}`) });
            })
            .catch(() => {});
        ask();
        // every 30 s since 02/11 (was a minute): the alert is only as quick as this poll
        const h = setInterval(ask, 30000);
        // the room was just read (62_Gcc.jsx markRead) — ask again now rather than in a minute (Max 30/10: the "2" stayed)
        window.addEventListener('gcc-read', ask);
        return () => { stop = true; clearInterval(h); window.removeEventListener('gcc-read', ask); };
    }, [gccOn, userId, location.pathname, location.search, navigate]);

    /* SYSTEM PROJECT (the Task Board) is Max's for now — Jay, 30/10: "Don't allow others to register theirs". The menu row
       shows to whoever may create tasks, and to whoever has something given or shared to them; asked once a session. */
    const [taskAccess, setTaskAccess] = useState(headerMemo.taskAccess || null);
    const tasksOn = TASKS_ON && Array.isArray(functions) && functions.includes('task_board') && !external;
    useEffect(() => {
        if (!tasksOn || !userId || headerMemo.taskAccess) return undefined;
        let stop = false;
        fetch(`${BACKEND_URL}/api/tasks/access`, { credentials: 'include', headers: { 'X-Portal-Passive': '1' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (!stop && d) { headerMemo.taskAccess = d; setTaskAccess(d); } })
            .catch(() => {});
        return () => { stop = true; };
    }, [tasksOn, userId]);

    /* CATCHY CHAT'S SETTINGS (Jay, task #99, 30/10: Knowledge Library, Lessons and Admin move into one Settings page, admin
       only) — drawn for whoever the AI Hub lets manage something: an ai_hub editor, or a department's manager, who keeps
       their own department's knowledge. Asked once a session, as the screen asks it (/teams). */
    const [aiHubManage, setAiHubManage] = useState(headerMemo.aiHubManage ?? null);
    const aiHubOn = AI_HUB_ON && Array.isArray(functions) && functions.includes('ai_hub') && !external;
    useEffect(() => {
        if (!aiHubOn || !userId || headerMemo.aiHubManage != null) return undefined;
        let stop = false;
        fetch(`${BACKEND_URL}/api/ai-hub/teams`, { credentials: 'include', headers: { 'X-Portal-Passive': '1' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (!stop && d) { headerMemo.aiHubManage = !!d.admin || (d.teams || []).some((x) => x.canManage); setAiHubManage(headerMemo.aiHubManage); } })
            .catch(() => {});
        return () => { stop = true; };
    }, [aiHubOn, userId]);

    const [isSidebarOpen, setIsSidebarOpen] = useState(() => (
        headerMemo.sidebarOpen !== null ? headerMemo.sidebarOpen
            : (typeof window === 'undefined' ? true : window.matchMedia('(min-width: 900px)').matches)
    ));
    useEffect(() => { headerMemo.sidebarOpen = isSidebarOpen; }, [isSidebarOpen]);

    // The sidebar comes back where it was scrolled to, before the first paint of the new screen.
    const sidebarRef = useRef(null);
    useLayoutEffect(() => {
        if (sidebarRef.current && headerMemo.sidebarScroll) sidebarRef.current.scrollTop = headerMemo.sidebarScroll;
    }, []);

    // Menu items are gated by the RBAC function grants returned from the auth-check.
    const reportItems = [
        ...(can('product_sales_dashboard') ? [{ label: t('nav.productSalesDashboard'), path: '/product-sales-dashboard' }] : []),
        ...(can('sales_report_dashboard') ? [{ label: t('nav.salesReportDashboard'), path: '/sales-report-dashboard' }] : []),
        ...(can('demand_forecast_dashboard') ? [{ label: t('nav.demandForecastDashboard'), path: '/demand-forecast-dashboard' }] : []),
        /* The stock dashboards are Data Explorer reports shared with everybody (2026-10-24, Max: "những mẫu báo cáo của
           inventory thì nên bỏ vào đây … để mn có thể xem chứ ko bỏ vào module tui làm") — read here, beside the sales
           dashboards; the tools that make them stay under Other tools. `inventory_dashboard` is the readers' grant. */
        ...(EXPLORER_ON && can('data_explorer') ? [{ label: t('nav.dataExplorer'), path: '/data-explorer' }] : []),
        /* OTHER TOOLS IS GONE (Jay, task #99, 30/10: "Other tools is a catch-all"). Its five rows went where their work is:
           the Global Inventory Report and the sales data load that feeds these reports here (Max, 30/10: Sales Data Update
           "move to Sales Report"), Sales Op. Python to Sales Operation, the frames' close and EUSU stock to Production.
           Paths and grants are unchanged — only where the row is drawn. */
        ...(INVENTORY_ON && can('inventory_report') ? [{ label: t('nav.inventoryReport'), path: '/inventory-report' }] : []),
        ...(SALES_CW_ON && can('sales_cw_load') ? [{ label: t('nav.salesDataLoad'), path: '/sales-data-load' }] : []),
        /* Sales Team 1's buyer / country report (task #96, Anh Le 29/10) — read from SAP every month. */
        ...(can('sales_buyer_country') ? [{ label: t('xSalesBuyer.nav'), path: '/sales-by-buyer' }] : []),
        ...(can('sales_model_color') ? [{ label: t('xModelColor.nav'), path: '/frames-by-model' }] : []),
    ];
    /* The handbooks are no longer sidebar rows (Max 01/11): every module carries its own Manual button (ModuleHelp.jsx),
       which opens the manual over the screen or, on a right-click, at /manual/<key> in a tab. */
    const developmentItems = [
        ...(can('sample_tracker') ? [{ label: t('nav.dashboard'), path: '/development/sample-tracker?tab=dash' }] : []),
        ...(can('sample_tracker') ? [{ label: t('nav.sampleTracker'), path: '/development/sample-tracker?tab=sample' }] : []),
        /* A partner company's own sample track (Jay, 2026-10-17). For HEVN it sits under the tracker it
           mirrors; for a partner it is the whole menu. */
        ...(PARTNER_ON && can('partner_samples') ? [{ label: t('nav.partnerSamples'), path: '/partner-samples' }] : []),
        // What the rules and the AI found wrong in both trackers (Jay's pattern, 2026-10-14).
    ];
    // Project Tracker / Ticket Station / Project Approval are the Sales OP workflow, so they live
    // under their own module rather than under Development. The routes are unchanged.
    // Ordered the way the work actually flows: create the project, get it approved, then raise tickets.
    /* THE CEO'S OWN FOLDER — Jay, 2026-09-23: "move CEO Office out of Sales Operation into its own
       folder. Put Project Approval and Pricing Approval in that folder too. Young should only need
       to open one folder. Please do this for the CEO role only."

       KEYED ON THE ACTIVE ROLE, not on a grant. What Young may DO is unchanged — the grants and
       every endpoint behind them are untouched; this is only where the three entries are drawn, and
       an admin who switches into CEO sees the same arrangement, which is how it gets checked.
       `activeRole` and not `roles`: Jay holds eleven roles and would otherwise never see Sales
       Operation's own copy of these again.

       They MOVE rather than being duplicated. Two paths to one screen in one sidebar is the thing
       somebody reports as "it is in two places, which one is the real one". */
    const ceoFolder = activeRole === 'CEO';
    /* A merged module's one entry (moduleTabsConfig.js): it opens the first screen the reader may open and
       stays lit on any of them, which a single path could not do. */
    const mergedItem = (key) => {
        const item = moduleMenuItem(key, can, t);
        return item ? [{ ...item, active: item.match.some((tab) => tabActive(tab, location.pathname)) }] : [];
    };
    const salesOpItems = [
        ...(can('project_tracker') ? [{ label: t('nav.projectTracker'), path: '/development/sample-tracker?tab=project' }] : []),
        // Only the people with something to do there: the CEO (approve / reject) and the team
        // managers (they resubmit a rejected project and watch their own queue). A regular member
        // has no action on this screen, so it stays out of their menu.
        ...(!ceoFolder && (can('project_tracker_approve') || can('project_tracker_create'))
          ? [{ label: t('nav.projectApproval'), path: '/development/project-approval' }] : []),
        ...(can('ticket_station') ? [{ label: t('nav.ticketStation'), path: '/development/sample-tracker?tab=tickets' }] : []),
        /* SALES OPERATION, RE-ALLOCATED — Jay Jung, 2026-10-21: "So many menu under Sales Operations. Please
           allocate again." Ten entries for an admin became five: the three pricing screens are ONE entry under
           Costing (they are costing, beside OPA/SCT), the courier pair is ONE entry here with its two screens as
           tabs, and CEO Office moved to the CEO folder for everybody who holds it (it is the CEO's section, not
           Sales Operation's work). Courier stays here: it is Global Sales Operation's (Tina, 2026-10-19; Max kept it here
           on 2026-10-30 when task #99 listed it for HEVN Admin). */
        ...mergedItem('courier'),
        /* Straight to the Sales Op screen (2026-10-25, Max on the one-button "SOP Team" page: "bỏ step bấm nút này dc hem") —
           with the new screen on; production's older screen keeps its menu page until then. Here since 30/10 (Other tools). */
        ...(can('auto_jobs') ? [{ label: t('nav.autoJobs'), path: process.env.REACT_APP_SOP_WEEKLY === '1' && can('auto_jobs_sop_team') ? '/run-python-sop-team' : '/auto-jobs-menu' }] : []),
    ];
    /* ONE FOLDER, three entries, in the order Young works: the queue of projects waiting on him,
       the queue of quotes waiting on him, then his own section. Drawn only while the active role is
       CEO — everybody else keeps these exactly where they have always been, because Sales OP still
       needs them and the desks should not see the approval screens at all. */
    /* CEO Office is in this folder for EVERYBODY who holds it since 2026-10-21 (the desks, the admins), not
       only while the active role is CEO — it left Sales Operation in the re-allocation above. The two approval
       queues still join it only for the CEO. */
    const ceoItems = [
        ...(ceoFolder && (can('project_tracker_approve') || can('project_tracker_create'))
          ? [{ label: t('nav.projectApproval'), path: '/development/project-approval' }] : []),
        ...(ceoFolder && PRICING_APPROVAL_ON && can('pricing_approval')
          ? [{ label: t('nav.pricingApproval'), path: '/sales/pricing-approval' }] : []),
        ...(can('ceo_office') ? [{ label: t('nav.ceoOffice'), path: '/ceo-office' }] : []),
    ];

    /* Costing (Max, 2026-08-29). Its own module rather than another entry under Sales Operation: OPA
       is a manufacturing document, the pricing calculator is a commercial one, and what they share is
       that both are costing. Same function key for now — one grant covers the pair. */
    /* The Document Library — its own top-level module, not an item under Development. The people it
       exists for (the regional RTMs, HINC) will hold this key and nothing else, and a module with one
       row in it reads better than their whole portal being a single line inside somebody else's
       menu. Jay, 2026-09-08: "a read-only role that sees the Document Library and nothing else". */
    const documentItems = [
        // The module itself is called Documents, so the row inside it says which screen it opens —
        // a module and its only item sharing a name reads like a rendering fault.
        ...(can('document_library') ? [{ label: t('nav.documentLibrary'), path: '/documents' }] : []),
    ];
    /* GTM — its own module rather than a row under Sales Operation. A masterplan is read by
       product, marketing and the regions, and burying it inside one of them is how a screen comes
       to be opened by nobody. Jihwan Kang via Max, 2026-10-05. */
    /* A BUILD SWITCH BESIDE THE PERMISSION — found on 2026-10-07 while merging this branch back.
       The backend mounts GTM only where GTM_MODULE is set, with its own note that a feature held
       back by nothing but a pending restart is held back only until the next unrelated one. The
       SCREEN had no such switch: it was gated on `can('gtm_programme')` alone, and that function
       is in the registry on production with Admin holding it at edit. So the first production
       bundle built from master would have drawn a GTM menu whose every call answers 404.
       The two switches are now the same shape — deploy_staging.sh sets REACT_APP_GTM, and
       deploy_production.sh deliberately does not. */
    /* ONE ENTRY since 2026-10-21 (Jay: "unify request/approval in 1 menu and split inside"): the author's list,
       the approver's queue and the tracker are still three screens on their own grants — now tabs at the top of
       each (ModuleTabs), drawn by the same grants that drew three menu rows. */
    const gtmItems = mergedItem('gtm');
    /* FINANCE — the Cash Report (2026-10-08, Tuan Nguyen). `cash_report` reaches the people on
       the module's own User list without a portal grant: /portal-auth adds it for them. */
    const financeItems = [
        /* Import first: it is where the figures come in, and the three reports below it are made
           from what was imported there. */
        ...(CASH_ON && can('cash_report') ? [{ label: t('nav.cashImport'), path: '/cash/import' }] : []),
        // Then where the figures are decided, then the reports they make (2026-10-15).
        ...(CASH_ON && can('cash_report') ? [{ label: t('nav.cashApproval'), path: '/cash/approval' }] : []),
        // The three reports, ONE module with a tab each (Max, 2026-10-15: "Gộp … thành 1 module thui").
        ...(CASH_ON && can('cash_report') ? [{ label: t('nav.cashReport'), path: '/cash/report' }] : []),
        ...(CASH_ON && can('cash_report') ? [{ label: t('nav.cashSetup'), path: '/cash/setup' }] : []),
    ];
    /* HEVN ADMIN — the company cars' Driver's Log (Jay, 2026-10-19), moved in from his Apps Script app. A driver
       holds driver_log (from /portal-auth, by being linked to a car); an admin holds driver_log_admin. */
    /* SALES PLANNING (2026-10-20) — one screen per person who takes part, in the order the work is done. */
    const salesPlanningItems = SALESPLAN_ON
        ? SPL_SCREENS.filter((sc) => sc.grants.some((g) => can(g))).map((sc) => ({ label: t(`nav.${sc.nav}`), path: sc.path }))
        : [];
    const costingItems = [
        ...(can('opa_sct') ? [{ label: t('nav.opaSct'), path: '/costing/opa-sct' }] : []),
        /* The pricing calculator, its approval queue and the inline cost sheets — one entry, three tabs
           (2026-10-21, moved from Sales Operation). The CEO keeps Pricing Approval in his own folder too. */
        ...mergedItem('pricing'),
    ];
    const productionItems = [
        ...(can('nfc_portal') ? [{ label: t('nav.nfcTokenPortal'), path: '/production/nfc' }] : []),
        // Style Hub is gated by its own function key, so it stays hidden for everyone it has not been
        // granted to - including on production, while it is still being built on the test server.
        ...(can('sp_hub') ? [{ label: t('xShell.styleHub'), path: '/production/sp-hub' }] : []),
        // The sheet the production team works in every day, so it is one click from the menu rather than a
        // tab inside the Hub. Same key as the Hub: it reads the same ledgers.
        ...(can('sp_master_sheet') ? [{ label: t('xShell.masterSheet'), path: '/production/master-sheet' }] : []),
        /* Capacity has its own key rather than riding on sp_hub: the vendors will need a login here
           and must never be given the Hub. */
        ...(can('capacity') ? [{ label: t('xShell.capacity'), path: '/production/capacity' }] : []),
        /* The shipping document, read by the people who receive the goods. Its own key rather than
           capacity's: a capacity grant should not hand anyone the vendor prices on it. */
        ...(can('sap_ship_doc') ? [{ label: t('xShell.sapShipDoc'), path: '/production/sap-ship-doc' }] : []),
        /* SUPPLY PLANNING — Jay, 28/10: "put this under the Production menu as Supply Planning, with NOOS as the first view …
           so it can grow with the SCM project without renaming". The NOOS dashboard (27/10, Other tools until then) is its
           first view; /noos still opens it. */
        ...(NOOS_ON && can('noos_dashboard') ? [{ label: t('xNoos.nav'), path: '/production/supply-planning' }] : []),
        // Production Status (PSR, Jay 01/11): HEVN reads every vendor; a vendor account holds psr_vendor and sees its own rows
        ...(PSR_ON && (can('production_status') || can('psr_vendor')) ? [{ label: t('xPsr.nav'), path: '/production-status' }] : []),
        /* From Other tools (Jay, task #99, 30/10): the frames' monthly close and the EUSU non-selling stock are stock the
           production side answers for. */
        ...(POLE_ON && can('pole_inventory') ? [{ label: t('nav.poleInventory'), path: '/pole-inventory' }] : []),
        /* EUSU Non-Selling Stock (2026-10-26). */
        ...(EUSU_ON && can('eusu_stock') ? [{ label: t('nav.eusuStock'), path: '/eusu-stock' }] : []),
        /* The vendors' own screen. Listed for Helinox staff too — somebody has to be able to check what
           a factory typed — but a vendor login holds ONLY this key, so this is the whole menu it sees. */
        ...(can('capacity_vendor') || can('capacity')
          ? [{ label: t('xShell.dailyOutput'), path: '/production/output' }] : []),
    ];
    /* PRODUCT LINE — its own top-level menu since 30/10 (Jay, task #99), out of Production. The mapping is keyed on the
       material master Style Hub imports, so it still rides on Style Hub's key; HINC reads the mapped list while the
       Product Line team works the open ones. */
    const productLineItems = [
        ...(can('sp_hub') ? [{ label: t('xShell.productLine'), path: '/production/product-line' }] : []),
    ];

    const goTo = (path) => {
        navigate(path);
        if (window.matchMedia('(max-width: 899px)').matches) {
            setIsSidebarOpen(false);
        }
    };

    // Both modules point at the same screen with a different ?tab=, so "which item is active" is
    // decided by that tab rather than by the path alone.
    const isTrackerTabActive = (itemPath) => {
        // An `external` item (the user manual) carries no route, and itemPath.includes() below would
        // throw on undefined while the tracker screen is open.
        if (!itemPath) return false;
        const fullPath = `${location.pathname}${location.search}`;
        if (fullPath === itemPath) return true;
        /* This runs for the WHOLE Sales Operation module, not just the tracker's tabs. An item with no
           ?tab= of its own — the pricing calculator, the approval screen — was matched on the full
           path including the query string, so it lost its highlight the moment a link carried one:
           /sales/pricing-calculator?open=CP-000011 lit nothing (Max, 2026-09-11). Those items are a
           plain path match. */
        if (!itemPath.includes('?')) return location.pathname === itemPath;
        if (location.pathname !== '/development/sample-tracker') return false;
        const tab = new URLSearchParams(location.search).get('tab');
        // no ?tab= lands on the Dashboard tab, same default the tracker screen uses
        if (itemPath.includes('tab=dash')) return !tab || tab === 'dash' || tab === 'dashboard';
        if (itemPath.includes('tab=sample')) return tab === 'sample';
        if (itemPath.includes('tab=project')) return tab === 'project';
        if (itemPath.includes('tab=tickets')) return tab === 'tickets';
        return false;
    };
    const isDevelopmentItemActive = isTrackerTabActive;
    /* AI HUB (2026-10-27) — ONE row: the screen's own tabs (Ask AI, Knowledge, Lessons, Activity) are the way between its
       parts (Max: "đã gộp cái task này vào Ask AI rùi thì ko cần này nữa nhé"); the row stays lit on every tab */
    const aiHubTab = location.pathname === '/ai-hub' ? (new URLSearchParams(location.search).get('tab') || 'ask') : null;
    /* Since 30/10 (Jay, task #99) the chat is one row and Settings — Knowledge Library, Lessons, Admin — another, drawn
       only for the people who manage something there (aiHubManage above); the screen keeps the rest on the chat. */
    const aiHubItems = AI_HUB_ON && can('ai_hub') ? [{ label: t('xAiHub.navAsk'), path: '/ai-hub?tab=ask', menuKey: '/ai-hub?tab=ask', active: aiHubTab === 'ask' }] : [];
    // GCC — Group Chat with Catchy (Jay, 2026-10-27), staging only for now
    if (GCC_ON && can('ai_hub')) aiHubItems.push({ label: t('xGcc.nav'), path: '/gcc', active: location.pathname === '/gcc', count: gccUnread });
    if (AI_HUB_ON && can('ai_hub') && aiHubManage) aiHubItems.push({ label: t('xAiHub.navSettings'), path: '/ai-hub?tab=lessons', menuKey: 'ai-hub-settings', active: !!aiHubTab && aiHubTab !== 'ask' });

    /* THE FOLDERS, IN THE PORTAL'S OWN ORDER, then put in the order this person arranged (menuOrder.js). The CEO's
       folder leads by default — the point of it is that Young opens one thing. Costing sits under Sales Operation
       since 2026-10-21: the pricing screens moved into it. A folder with nothing the reader may open is not drawn. */
    const samePath = (path) => location.pathname === path;
    // the Task Board is everybody's own page, so it is a folder of its own near the top rather than a row inside a module
    const taskItems = TASKS_ON && can('task_board') && taskAccess && (taskAccess.canCreate || taskAccess.visible > 0) ? [{ label: t('xTask.nav'), path: '/tasks', active: location.pathname === '/tasks' }] : [];
    /* HEVN ADMIN — the company cars' Driver's Log (Jay, 2026-10-19), moved in from his Apps Script app. A driver holds
       driver_log (from /portal-auth, by being linked to a car); an admin holds driver_log_admin. Since 30/10 (Jay, task
       #99) it also holds System Project, which had a folder of its own. */
    const hevnAdminItems = [
        ...taskItems,
        ...(DRIVERLOG_ON && (can('driver_log') || can('driver_log_admin')) ? [{ label: t('nav.driverLog'), path: '/driver-log' }] : []),
        // Staff Purchase (task #1132): every Helinox employee applies here; approvers and Tina work the same screen.
        ...(STAFFBUY_ON && can('staff_purchase') ? [{ label: t('xStaffBuy.nav'), path: '/staff-purchase' }] : []),
        // what the portal's AI costs, against the caps (Jay #1179: "move to HEVN Admin"; moved out of AI Hub settings)
        ...(can('ai_usage') ? [{ label: t('xAiUse.nav'), path: '/admin/ai-usage' }] : []),
    ];
    const folderDefs = [
        { id: 'ceoFolder', icon: <BriefcaseBusiness size={18} strokeWidth={2} />, label: t('nav.ceoFolder'), items: ceoItems,
          isActive: isTrackerTabActive, containsActive: ceoItems.some((i) => i.active ?? isTrackerTabActive(i.path)) },
        { id: 'report', icon: <BarChart3 size={18} strokeWidth={2} />, label: t('nav.salesReport'), items: reportItems,
          isActive: samePath, containsActive: reportItems.some((i) => samePath(i.path)) },
        { id: 'development', icon: <FlaskConical size={18} strokeWidth={2} />, label: t('nav.development'), items: developmentItems,
          isActive: isDevelopmentItemActive, containsActive: developmentItems.some((i) => isDevelopmentItemActive(i.path)) },
        { id: 'salesOp', icon: <ClipboardList size={18} strokeWidth={2} />, label: t('nav.salesOperation'), items: salesOpItems,
          isActive: isTrackerTabActive, containsActive: salesOpItems.some((i) => i.active ?? isTrackerTabActive(i.path)) },
        { id: 'costing', icon: <Timer size={18} strokeWidth={2} />, label: t('nav.costing'), items: costingItems,
          isActive: samePath, containsActive: costingItems.some((i) => i.active ?? samePath(i.path)) },
        { id: 'production', icon: <Factory size={18} strokeWidth={2} />, label: t('nav.production'), items: productionItems,
          isActive: samePath, containsActive: productionItems.some((i) => samePath(i.path)) },
        { id: 'productLine', icon: <Layers size={18} strokeWidth={2} />, label: t('xShell.productLine'), items: productLineItems,
          isActive: samePath, containsActive: samePath('/production/product-line') },
        { id: 'aiHub', icon: <BotMessageSquare size={18} strokeWidth={2} />, label: t('xAiHub.nav'), items: aiHubItems,
          isActive: () => false, containsActive: !!aiHubTab },
        { id: 'documents', icon: <FileText size={18} strokeWidth={2} />, label: t('nav.documents'), items: documentItems,
          isActive: (path) => location.pathname.startsWith(path), containsActive: location.pathname.startsWith('/documents') || samePath('/manual/documents') },
        /* By SEGMENT, not by prefix: '/gtm-approval' starts with '/gtm', so a prefix test lit Programmes and Approval
           together on the approver's screen. A programme's own page ('/gtm/GTM-26-001') still lights its list. */
        { id: 'gtm', icon: <Target size={18} strokeWidth={2} />, label: t('nav.gtm'), items: gtmItems,
          isActive: (path) => samePath(path) || location.pathname.startsWith(`${path}/`), containsActive: location.pathname.startsWith('/gtm') },
        { id: 'finance', icon: <Landmark size={18} strokeWidth={2} />, label: t('nav.finance'), items: financeItems,
          isActive: (path) => samePath(path) || location.pathname.startsWith(`${path}/`), containsActive: location.pathname.startsWith('/cash') },
        { id: 'salesPlanning', icon: <CalendarRange size={18} strokeWidth={2} />, label: t('nav.salesPlanning'), items: salesPlanningItems,
          isActive: samePath, containsActive: location.pathname.startsWith('/sales-planning') },
        { id: 'hevnAdmin', icon: <Building2 size={18} strokeWidth={2} />, label: t('nav.hevnAdmin'), items: hevnAdminItems,
          isActive: samePath, containsActive: hevnAdminItems.some((i) => i.active ?? samePath(i.path)) },
    ];
    const folders = applyMenuOrder(folderDefs.filter((f) => f.items.length > 0), menuOrder);

    const [arranging, setArranging] = useState(false);
    // Only what the person can see is sent; what this role hides keeps its place from before (mergeMenuOrder).
    const saveMenuOrder = async (draft) => {
        const order = draft ? mergeMenuOrder(menuOrder, draft) : null;
        const res = await fetch(`${BACKEND_URL}/api/portal/menu-order`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || t('nav.arrangeFailed'));
        setMenuOrderCache(body.menuOrder || null);
        setArranging(false);
    };

    /* Once a minute at most: it ran on every render of every screen's header (it followed `functions`, a new
       array each render), which is a request per navigation for a badge. */
    const mayManageUsers = can('user_management');
    useEffect(() => {
        if (!mayManageUsers || Date.now() - headerMemo.accessAt < 60000) return;
        headerMemo.accessAt = Date.now();
        /* the badge counts everything waiting there: the form's requests, the roles teams ask for, and the partner accounts that
           cannot sign in until approved (Jay #1160 / #1155) */
        Promise.all([
            fetch(`${BACKEND_URL}/api/access-requests?status=pending`, { credentials: 'include' }).then((r) => r.json()).then((b) => (b.rows || []).length).catch(() => 0),
            fetch(`${BACKEND_URL}/api/admin/signup-counts`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : {})).then((b) => (Number(b.roles) || 0) + (Number(b.partners) || 0)).catch(() => 0),
        ]).then(([a, b]) => { headerMemo.accessPending = a + b; setAccessPending(headerMemo.accessPending); })
            .catch(() => setAccessPending(0));
    }, [mayManageUsers]);

    useEffect(() => {
        document.body.classList.add('portal-shell');
        return () => {
            document.body.classList.remove('portal-shell', 'portal-sidebar-open');
        };
    }, []);

    useEffect(() => {
        document.body.classList.toggle('portal-sidebar-open', isSidebarOpen);
    }, [isSidebarOpen]);

    return (
        <div>
            <header className="header-container">
                {/* decoration only (season.js) — shown by theme-christmas.css under html[data-season], hidden from readers */}
                <span className="xmas-snow" aria-hidden="true" />
                <span className="xmas-garland" aria-hidden="true" />
                <XmasClick />
                <button
                    type="button"
                    className="hamburger-button"
                    aria-label={t('xShell.toggleNav')}
                    aria-expanded={isSidebarOpen}
                    onClick={() => setIsSidebarOpen((open) => !open)}
                >
                    <Menu size={22} strokeWidth={2} />
                </button>
                {/* Both wordmarks ship and CSS picks one, rather than choosing in JS: the theme is applied
                    to <html> by an inline script before React mounts, so a CSS swap is already correct on
                    the first paint and never flashes the wrong logo. The black one is invisible on a dark
                    header, which is the whole reason for the second asset. */}
                {/* ONE LINE OF TYPE (Jay, 2026-10-21: "still unaligned"). The wordmark, the title and the
                    badges share one text line, so the browser itself puts them on one baseline — each logo
                    PNG is lowered by the space under its own letters (style.css), whatever font the reader's
                    machine draws HEVN PORTAL in. */}
                <div className="brand">
                    <span className="logo-container" onClick={() => navigate('/')}>
                        <img src={logo} alt="Helinox" className="logo logo-light" />
                        <img src={logoWhite} alt="" aria-hidden="true" className="logo logo-dark" />
                    </span>
                    <span className="app-name">HEVN PORTAL</span>
                    {/* Jay, 2026-10-21: "Add Ai Powered di. Just change the Ngoi Sao to Claude's icon" — his
                        badge as drawn, with Claude's spark where the star was. */}
                    <span className="ai-powered" title={t('nav.aiPowered')} data-testid="ai-powered">
                        <AiTag size={13} /><span className="ai-powered-txt">AI-POWERED</span>
                    </span>
                    {/* The staging site is the same app on port 3000 with its own build + backend (5001).
                        Without a marker it is impossible to tell which environment you are looking at. */}
                    {typeof window !== 'undefined' && window.location.port === '3000' && (
                        <span className="env-badge" title={t('xShell.testServer')}>TEST</span>
                    )}
                </div>
                <div className="user-info">
                    {/* The quote of the day, just before the bell — Max, 2026-10-15, after the DAC board,
                        which carries it in its header. It was a strip at the top of five pages, in three
                        languages; here it is one line in the portal's language. The Sample / Project
                        Tracker keeps its developer quotes, as the strip there did. */}
                    {userId && internal && (
                        <QuoteOfTheDay
                            variant="header"
                            audience={location.pathname.startsWith('/development/sample-tracker') ? 'developer' : 'portal'}
                        />
                    )}
                    {/* The bell (Max, 2026-10-14): what the portal has mailed this person, where the work is.
                        NotificationBell.jsx says how it is fed and why it never extends the session. */}
                    {/* feedback from every screen (Jay #1138, FeedbackButton.jsx) */}
                    {userId && internal && <FeedbackButton />}
                    {userId && internal && <NotificationBell />}
                    {/* The HEVN Assistant: a corner bubble on every page (AskBubble.jsx, 2026-10-14). */}
                    {/* not on the AI Hub: the page is itself the place to ask, and the bubble sat on its Ask button (27/10) */}
                    {userId && internal && can('ai_assistant') && location.pathname !== '/ai-hub' && location.pathname !== '/gcc' && <AskBubble canHub={AI_HUB_ON && can('ai_hub')} />}
                    {/* Language: a globe and a list since 2026-10-21 (LanguagePicker.jsx) — four languages, and one
                        click can no longer say which. Purely client-side, like the switch it replaced. */}
                    <LanguagePicker />

                    {/* The Christmas switch (Max 31/10): the look and the snow, each its own switch — WCAG 2.2.2 wants moving
                        content that can be stopped. Native checkboxes, so the keyboard and screen readers get them for free. */}
                    {season.available && (
                        <div className="xmas-switch" ref={xmasRef}>
                            <button type="button" className="theme-toggle xmas-toggle" aria-expanded={xmasOpen} aria-haspopup="true"
                                title={t('xShell.xmasMenu')} aria-label={t('xShell.xmasMenu')} onClick={() => { setXmasOpen((v) => !v); if (xmasTip) closeXmasTip(); }} data-xmas-toggle>
                                <TreePine size={15} />
                            </button>
                            {season.on && xmasTip && !xmasOpen && (
                                <div className="xmas-tip" role="dialog" aria-label={t('xShell.xmasTipTitle')} data-xmas-tip>
                                    <b>{t('xShell.xmasTipTitle')}</b>
                                    <p>{t('xShell.xmasTipBody')}</p>
                                    <div className="xmas-tip-acts">
                                        <button type="button" className="xmas-tip-off" onClick={() => { season.setOn(false); closeXmasTip(); }} data-xmas-tip-off>{t('xShell.xmasTipOff')}</button>
                                        <button type="button" className="xmas-tip-ok" onClick={closeXmasTip} data-xmas-tip-ok>{t('xShell.xmasTipOk')}</button>
                                    </div>
                                </div>
                            )}
                            {xmasOpen && (
                                <div className="xmas-menu" role="group" aria-label={t('xShell.xmasMenu')} data-xmas-menu>
                                    <label className="xmas-opt">
                                        <input type="checkbox" role="switch" checked={season.on} onChange={(e) => season.setOn(e.target.checked)} data-xmas-on />
                                        <span>{t('xShell.xmasOn')}</span>
                                    </label>
                                    <label className={`xmas-opt${season.on ? '' : ' off'}`}>
                                        <input type="checkbox" role="switch" checked={season.snow} disabled={!season.on} onChange={(e) => season.setSnow(e.target.checked)} data-xmas-snow />
                                        <span>{t('xShell.xmasSnow')}</span>
                                    </label>
                                    <p className="xmas-note">{t('xShell.xmasNote')}</p>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Theme switch. Flips one attribute on <html>; every colour comes from static CSS, so it
                        cannot affect how fast data loads. */}
                    <button
                        type="button"
                        className="theme-toggle"
                        title={theme === 'dark' ? t('common.light') : t('common.dark')}
                        aria-label={theme === 'dark' ? t('common.light') : t('common.dark')}
                        onClick={toggleTheme}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    {roles.length > 1 && (
                        <select
                            className="role-switcher"
                            title={t('xShell.roleSwitch')}
                            value={activeRole || '__all__'}
                            onChange={(e) => setActiveRole(e.target.value)}
                        >
                            <option value="__all__">{t('common.allRoles')}</option>
                            {/* The LABEL, the key as the value — Max, 2026-09-16: renaming a role in
                                Role & Permissions left this one dropdown still printing the internal
                                id. Falls back to the key, so a role whose name was never set reads
                                as something rather than as an empty option. */}
                            {roles.map((r) => <option key={r} value={r}>{roleNames[r] || r}</option>)}
                        </select>
                    )}
                    {userId && <UserAvatar url={avatarUrl} name={name} size={30} className="header-avatar" />}
                    <span className="username">{name}</span>
                    <div className="menu">
                        <span className="dots">•••</span>
                        <div className="dropdown">
                            {internal && (
                            <button 
                                className="dropdown-item" 
                                onClick={() => navigate(`/profile/${userId}`)}
                            >
                                {t('account.profile')}
                            </button>
                            )}
                            {can('user_management') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/list-user')}
                                >
                                    {t('account.listUsers')}
                                </button>
                            )}
                            {can('user_management') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/register')}
                                >
                                    {t('account.createUser')}
                                </button>
                            )}
                            {/* THE QUEUE HAS TO BE FINDABLE — Max, 2026-10-07: "admin vào
                                /admin/access-requests xem hàng chờ ko thấy á", on staging, where
                                the screen was working. He was looking in this menu and it was not
                                in it, so the only way in was knowing the URL. A queue nobody is
                                told about is a queue nobody reads, and a partner would be waiting
                                on a decision nobody knows they are supposed to make.
                                THE COUNT IS THE POINT, not decoration: the difference between
                                somebody waiting and nobody waiting is the whole reason to open
                                it, and without it this is one more line to read past. */}
                            {can('user_management') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/admin/access-requests')}
                                >
                                    {t('account.accessRequests')}
                                    {accessPending > 0 && (
                                        <span className="hdr-badge">{accessPending}</span>
                                    )}
                                </button>
                            )}
                            {can('role_management') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/admin/roles')}
                                >
                                    {t('account.rolesPermissions')}
                                </button>
                            )}
                            {(can('usage_analytics') || can('quote_management')) && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/admin/portal-insights')}
                                >
                                    {t('account.portalInsights')}
                                </button>
                            )}
                            {can('improvement_loop') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/admin/improvement-loop')}
                                    data-hdr-loop
                                >
                                    {t('xLoop.title')}
                                </button>
                            )}
                            {can('ai_usage') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/admin/ai-usage')}
                                >
                                    {t('xAiUse.nav')}
                                </button>
                            )}
                            {can('master_data') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/admin/master-data')}
                                >
                                    {t('account.masterData')}
                                </button>
                            )}
                            {can('audit_log') && (
                                <button
                                    className="dropdown-item"
                                    onClick={() => navigate('/activity-log')}
                                >
                                    {t('account.activityLog')}
                                </button>
                            )}
                            <div className="separator"></div>
                            <button onClick={logout} className="dropdown-item logout">{t('account.logout')}</button>
                        </div>
                    </div>
                </div>                    
            </header>
            <aside className={`portal-sidebar ${isSidebarOpen ? 'open' : ''}`} aria-label={t('xShell.portalNav')}
                ref={sidebarRef} onScroll={(e) => { headerMemo.sidebarScroll = e.currentTarget.scrollTop; }}>
                {/* the season's snow behind the menu (theme-christmas.css) — sticky and 0 tall, so it never lengthens the scroll */}
                <span className="xmas-side-snow" aria-hidden="true"><i /></span>
                {arranging ? (
                    <SidebarArrange folders={folders} customised={!!menuOrder}
                        onSave={saveMenuOrder} onReset={() => saveMenuOrder(null)}
                        onCancel={() => setArranging(false)} />
                ) : (
                    <>
                        {folders.map((f) => (
                            <SidebarModule key={f.id} id={f.id} icon={f.icon} label={f.label} items={f.items}
                                isActive={f.isActive} containsActive={f.containsActive} onNavigate={goTo} />
                        ))}
                        {/* Each person's own order (Jay, 2026-10-21). Not for a partner or a driver: their menu is
                            one screen, and the gate refuses them the route that stores it. */}
                        {internal && !driverOnly && folders.length > 1 && (
                            <button type="button" className="sidebar-arrange-open" data-testid="sidebar-arrange-open"
                                onClick={() => setArranging(true)}>
                                <ArrowUpDown size={15} strokeWidth={2} />
                                {t('nav.arrangeOpen')}
                            </button>
                        )}
                    </>
                )}
            </aside>
            {isSidebarOpen && (
                <button
                    type="button"
                    className="sidebar-backdrop"
                    aria-label={t('xShell.closeNav')}
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}
            <style jsx="true">{`
                .logo-container {
                    cursor: pointer;
                }
                .logout {
                    font-weight: bold;
                }
                .role-switcher {
                    border: 1.5px solid var(--portal-border);
                    border-radius: 8px;
                    padding: 5px 10px;
                    font-size: 13px;
                    font-weight: 700;
                    color: var(--portal-text);
                    background: var(--portal-surface);
                    cursor: pointer;
                    max-width: 180px;
                }
                .role-switcher:focus { outline: none; border-color: #2E75B6; }
                /* Fixed width so the header never reflows when the label changes between EN and 한국어. */
                /* The caret points down when the group is open and right when it is closed — without this the
                   header looks identical in both states and the control reads as broken. */
                .sidebar-module-caret {
                    margin-left: auto;
                    transition: transform .18s ease;
                }
                .sidebar-module.is-closed .sidebar-module-caret {
                    transform: rotate(-90deg);
                }
                .sidebar-module-header {
                    cursor: pointer;
                }
                /* The browser's own [hidden] rule sets display:none, but it lives in the UA stylesheet and the
                   display:flex on .sidebar-nav in style.css outranks it — so the group's items stayed on
                   screen while the caret happily rotated. This restates it at class specificity. */
                .sidebar-nav[hidden] {
                    display: none !important;
                }

                /* Square, same height as the language pill so the header keeps one baseline. */
                .theme-toggle {
                    width: 34px;
                    height: 31px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    box-sizing: border-box;
                    border: 1.5px solid var(--portal-border);
                    border-radius: 8px;
                    background: var(--portal-surface);
                    color: var(--portal-text);
                    cursor: pointer;
                }
                .theme-toggle:hover { border-color: #2E75B6; }

                /* The globe: the theme button's square, so the two sit as a pair. Its list opens under it,
                   right-aligned, over the page (the header is z-index 1000). */
                .lang-pick { position: relative; }
                .lang-globe {
                    width: 34px;
                    height: 31px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    box-sizing: border-box;
                    border: 1.5px solid var(--portal-border);
                    border-radius: 8px;
                    background: var(--portal-surface);
                    color: var(--portal-text);
                    cursor: pointer;
                }
                .lang-globe:hover, .lang-globe[aria-expanded="true"] { border-color: #2E75B6; }
                .lang-globe:focus-visible { outline: 2px solid #2E75B6; outline-offset: 1px; }
                .lang-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    right: 0;
                    z-index: 1100;
                    min-width: 196px;
                    padding: 6px;
                    border: 1px solid var(--portal-border);
                    border-radius: 12px;
                    background: var(--portal-surface);
                    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.16);
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .lang-opt {
                    display: grid;
                    grid-template-columns: 16px 24px 1fr;
                    align-items: center;
                    gap: 8px;
                    width: 100%;
                    padding: 8px 10px;
                    border: 0;
                    border-radius: 8px;
                    background: transparent;
                    color: var(--portal-text);
                    font-size: 14px;
                    text-align: left;
                    cursor: pointer;
                }
                .lang-opt:hover, .lang-opt:focus-visible { background: var(--portal-surface-hover, #f1f5f9); outline: none; }
                .lang-opt.on { background: #e8f2fc; }
                .lang-flag {
                    font-size: 10.5px;
                    font-weight: 700;
                    letter-spacing: 0.4px;
                    color: #6b7280;
                }
                /* The tick is named, not inherited: the global \`* { color }\` reaches the icon's own path. */
                .lang-tick, .lang-tick svg, .lang-tick svg * { color: #2E75B6; }
                .lang-tick { display: inline-flex; }
                html[data-theme="dark"] .lang-opt.on { background: #1d3347; }
                html[data-theme="dark"] .lang-flag { color: #9aa4b2; }
                html[data-theme="dark"] .lang-tick, html[data-theme="dark"] .lang-tick svg,
                html[data-theme="dark"] .lang-tick svg * { color: #7fb3e6; }
            `}</style>
        </div>
    );
};

export default Header;
